import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechApiEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests";
import { useAgTechHttpPostAndConvertRequest, useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { QueryClient } from "@tanstack/react-query";
import { Index } from "@agtech/core/utilities/AgTechUtilities";
import { CreatePlanningTaskDTO, SalePlanningTask } from "./PlanningTaskData";
import { PlanningTaskDataGetMyTasksQuery, PlanningTaskDataGetSaleTasksQuery } from "./PlanningTaskQueries";

// The cross-sale "My Tasks" list (MyPlanningTasksDialog) reads from a separate cache entry than the
// per-sale grid - assigning, completing, deleting, or creating a task from the per-sale grid (or
// vice versa) never touches the other view's cache on its own, so without this it only catches up
// on a full app refresh. Invalidated rather than patched in place: unlike the per-sale list, "am I
// still assigned to this and is it still incomplete" depends on the current user, which isn't known
// here, so a refetch is simpler and safer than guessing.
const invalidateMyTasks = (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({ queryKey: PlanningTaskDataGetMyTasksQuery.queryKey(undefined) });
}

export const usePlanningTaskCreateAction = (): AgTechDataAction<CreatePlanningTaskDTO, SalePlanningTask> => {
    // The posted DTO and the returned entity are different shapes (unlike useAgTechHttpPostEntityRequest,
    // which assumes both are the same type), so this uses the lower-level convert-based request to
    // unwrap the backend's Result<T> envelope down to .data while posting CreatePlanningTaskDTO.
    const createRequest = useAgTechHttpPostAndConvertRequest<CreatePlanningTaskDTO, AgTechApiEntityResponse<SalePlanningTask>, SalePlanningTask>();

    return {
        name: 'CreatePlanningTask',
        getConfiguration: () => ({
            actionExecutionMessage: 'Adding task...',
            actionConfirmationMessage: 'Task added'
        }),
        action: async props => {
            return await createRequest.post({
                path: `Sales/${props.submittedEntity.saleId}/PlanningTasks`,
                postedData: props.submittedEntity,
                convert: response => response.data
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: PlanningTaskDataGetSaleTasksQuery,
                queryContext: { saleId: props.submittedEntity.saleId },
                update: tasks => Index([...Object.values(tasks), props.responseData], task => task.id)
            });

            await invalidateMyTasks(props.queryClient);

            props.appContext.popups.closeAll();
        }
    }
}

export type PlanningTaskAssignActionDTO = {
    task: SalePlanningTask,
    userId: number | null
}

export const usePlanningTaskAssignAction = (): AgTechDataAction<PlanningTaskAssignActionDTO, boolean> => {
    const assignRequest = useAgTechHttpPostRequest<{ userId: number | null }, boolean>();

    return {
        name: 'AssignPlanningTask',
        getConfiguration: () => ({
            actionExecutionMessage: 'Assigning task...',
            actionConfirmationMessage: 'Successfully assigned task'
        }),
        action: async props => {
            return await assignRequest.post({
                path: `Sales/${props.submittedEntity.task.saleId}/PlanningTasks/${props.submittedEntity.task.id}/Assign`,
                postedData: { userId: props.submittedEntity.userId }
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: PlanningTaskDataGetSaleTasksQuery,
                queryContext: { saleId: props.submittedEntity.task.saleId },
                update: tasks => {
                    const updatedTasks = Object.values(tasks).map(task => task.id === props.submittedEntity.task.id
                        ? { ...task, assignedToUserId: props.submittedEntity.userId }
                        : task
                    );

                    return Index(updatedTasks, task => task.id);
                }
            });

            await invalidateMyTasks(props.queryClient);
        }
    }
}

export type PlanningTaskCompleteActionDTO = {
    task: SalePlanningTask,
    isComplete: boolean
}

export const usePlanningTaskCompleteAction = (): AgTechDataAction<PlanningTaskCompleteActionDTO, boolean> => {
    const completeRequest = useAgTechHttpPostRequest<{ isComplete: boolean }, boolean>();

    return {
        name: 'SetPlanningTaskComplete',
        getConfiguration: props => ({
            actionExecutionMessage: props.submittedEntity.isComplete ? 'Completing task...' : 'Reopening task...',
            actionConfirmationMessage: props.submittedEntity.isComplete ? 'Task marked complete' : 'Task reopened'
        }),
        action: async props => {
            return await completeRequest.post({
                path: `Sales/${props.submittedEntity.task.saleId}/PlanningTasks/${props.submittedEntity.task.id}/Complete`,
                postedData: { isComplete: props.submittedEntity.isComplete }
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: PlanningTaskDataGetSaleTasksQuery,
                queryContext: { saleId: props.submittedEntity.task.saleId },
                update: tasks => {
                    const updatedTasks = Object.values(tasks).map(task => task.id === props.submittedEntity.task.id
                        ? { ...task, isComplete: props.submittedEntity.isComplete }
                        : task
                    );

                    return Index(updatedTasks, task => task.id);
                }
            });

            await invalidateMyTasks(props.queryClient);
        }
    }
}

export type PlanningTaskDeleteActionDTO = {
    task: SalePlanningTask
}

export const usePlanningTaskDeleteAction = (): AgTechDataAction<PlanningTaskDeleteActionDTO, boolean> => {
    const deleteRequest = useAgTechHttpPostRequest<{}, boolean>();

    return {
        name: 'DeletePlanningTask',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting task...',
            actionConfirmationMessage: 'Successfully deleted task'
        }),
        confirm: async props => {
            return props.appContext.confirmations.askForConfirmation({
                content: {
                    header: 'Are you sure you want to delete this planning task?',
                    details: `"${props.submittedEntity.task.taskName}" will be permanently removed from this sale.`
                }
            });
        },
        action: async props => {
            return await deleteRequest.post({
                path: `Sales/${props.submittedEntity.task.saleId}/PlanningTasks/${props.submittedEntity.task.id}/Delete`,
                postedData: {}
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: PlanningTaskDataGetSaleTasksQuery,
                queryContext: { saleId: props.submittedEntity.task.saleId },
                update: tasks => {
                    const updatedTasks = Object.values(tasks).filter(task => task.id !== props.submittedEntity.task.id);

                    return Index(updatedTasks, task => task.id);
                }
            });

            await invalidateMyTasks(props.queryClient);

            props.appContext.popups.closeAll();
        }
    }
}
